import {
  triggerToast,
  ButtonDS,
  DownloadOutlined,
  TextCapitalized,
  LaunchPage,
  usePersistMuiTableState,
  MaterialUiTable,
  sortByDateTime,
  MaterialUiColumnsProps,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  bankStatementExportAsync,
  bankStatementsListAsync,
  documentDownloadAsync,
  selectBankStatementExportLink,
  selectBankStatementExportStatus,
  selectBankStatementsList,
  selectBankStatementsListStatus,
} from "./billingsSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { BankStatementsList, BankStatementsListDisplayed } from "./billingsAPI";
import { selectCompany } from "../homeSlice";
import "../../../i18n/i18n";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { documentDownloadXlsxAsync } from "../../../libSlice/downloadFileXslx";
import styled from "styled-components";

export const BankStatementTab = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const params = useParams();
  const company = useAppSelector(selectCompany);

  const bankStatementsListInit: BankStatementsList[] = useAppSelector(
    selectBankStatementsList,
  );
  const bankStatementsListStatus = useAppSelector(
    selectBankStatementsListStatus,
  );

  const bankStatementsExportStatus = useAppSelector(
    selectBankStatementExportStatus,
  );
  const bankStatementExportLink = useAppSelector(selectBankStatementExportLink);
  const [listDisplayed, setListDisplayed] = useState<
    BankStatementsListDisplayed[] | null
  >(null);

  const pageName = "bankStatements";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      date: "size",
      closingBalance: "size",
      bucketFileNameDownload: "size",
      bucketFileNameDownloadXlsx: "size",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const bankStatementsList: BankStatementsList[] = useMemo(() => {
    const rowsWithId = bankStatementsListInit.map((bankStatement) => {
      const newDict: BankStatementsList & { id: string } = {
        ...bankStatement,
        id: bankStatement.bucketFileNameDownload,
      };
      return newDict;
    });
    return sortByDateTime(rowsWithId, "desc", "date");
  }, [bankStatementsListInit]);

  useEffect(() => {
    if (bankStatementsList && bankStatementsListStatus === "success") {
      setListDisplayed(bankStatementsList);
    }
  }, [bankStatementsList, bankStatementsListStatus]);

  useEffect(() => {
    if (company && bankStatementsListStatus === "idle") {
      void dispatch(bankStatementsListAsync(company.uuid));
    }
  }, [dispatch, company, bankStatementsListStatus]);

  useEffect(() => {
    if (bankStatementsExportStatus === "success") {
      triggerToast(t("billings.bankStatement.upload.success") || "", "valid");
    } else if (bankStatementsExportStatus === "failed") {
      triggerToast(t("billings.bankStatement.upload.failure") || "", "error");
    }
  }, [bankStatementsExportStatus, t]);

  useEffect(() => {
    if (
      bankStatementExportLink.presignedUrl &&
      bankStatementExportLink.fileName
    ) {
      void dispatch(documentDownloadAsync(bankStatementExportLink));
    }
  }, [bankStatementExportLink, dispatch]);

  const columnsDisplayed: {
    [index in keyof BankStatementsListDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      date: {
        type: "date",
        width: 200,
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      closingBalance: {
        type: "number",
        width: 100,
        valueFormatter: (value: number | string | null) => {
          return value === null ? "-" : `${value} EUR`;
        },
      },
      bucketFileNameDownload: {
        type: "string",
        width: 200,
        valueFormatter: (value: string, row) => {
          return (
            value && (
              <StyledColumnButton>
                <ButtonDS
                  sizeButton={"S"}
                  format={"hug"}
                  buttonType={"secondary"}
                  singleIcon={{ icon: <DownloadOutlined />, size: "S" }}
                  onClick={() => {
                    void dispatch(
                      bankStatementExportAsync({
                        bucketFileName: value ?? "",
                        fileName:
                          "fileName" in row ? (row["fileName"] as string) : "",
                      }),
                    );
                  }}
                />
              </StyledColumnButton>
            )
          );
        },
      },
      bucketFileNameDownloadXlsx: {
        type: "string",
        width: 200,
        valueFormatter: (value: string, row) => {
          return (
            value && (
              <StyledColumnButton>
                <ButtonDS
                  sizeButton={"S"}
                  format={"hug"}
                  buttonType={"secondary"}
                  singleIcon={{ icon: <DownloadOutlined />, size: "S" }}
                  onClick={() => {
                    void dispatch(
                      bankStatementExportAsync({
                        bucketFileName: value ?? "",
                        fileName:
                          "fileName" in row
                            ? (row["fileName"] as string).split(".pdf")[0] +
                              ".xlsx"
                            : "",
                      }),
                    );
                  }}
                />
              </StyledColumnButton>
            )
          );
        },
      },
    };
  }, [dispatch]);

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("bankStatements.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  if (params.tab !== "bankStatements") {
    return;
  }

  const searchBarProps = {
    values: bankStatementsList,
    setFilterValues: setListDisplayed,
    keysToIgnore: [
      "bucketFileNameDownload" as const,
      "bucketFileNameDownloadXlsx" as const,
      "fileName" as const,
    ],
  };

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  return (
    <MaterialUiTable<
      keyof BankStatementsListDisplayed,
      BankStatementsListDisplayed
    >
      rows={listDisplayed}
      columns={columnsDisplayed}
      pageName={pageName}
      dataGridState={dataGridState}
      searchBar={searchBarProps}
      apiRef={apiRef}
      downloadViewAction={downloadViewAction}
    />
  );
};

const StyledColumnButton = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;
